import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {equals} from 'ramda';
import {getWindowContext, validateValue} from '../utils';

class InnerConsumerFunction extends Component {
    // shallow compare path to rerender whenever the path changes.
    // deep compare params so that host-app function can be rerun with same params.

    shouldComponentUpdate(nextProps) {
        return (
            !equals(this.props.path, nextProps.path) ||
            this.props.params !== nextProps.params
        );
    }
    render() {
        const {path: _path, setProps, contextValue, params} = this.props;

        const newContextValue = validateValue(
            _path,
            contextValue,
            'ConsumerFunction'
        );

        if (typeof newContextValue?.then === 'function') {
            throw new Error(
                `ConsumerFunction tried to access a path (${_path}) that returned a Promise. ` +
                    'Promises are not supported in ConsumerFunction ' +
                    'right now, but may be in the future.'
            );
        }

        if (typeof newContextValue === 'function' && params !== null) {
            setTimeout(() => setProps({value: newContextValue(...params)}), 0);
        }
        return null;
    }
}

/**
 * ConsumerFunction is a Dash Embedded component.
 * It takes a context `value`, `path`, and `params`, uses
 * run host-app functions inside of a dash app and pass
 * data from the dash app to the host app
 */
export default class ConsumerFunction extends Component {
    render() {
        const WindowContext = getWindowContext();
        if (!WindowContext) {
            return null;
        }
        return (
            <WindowContext.Consumer>
                {(contextValue) => (
                    <InnerConsumerFunction {...{...this.props, contextValue}} />
                )}
            </WindowContext.Consumer>
        );
    }
}

ConsumerFunction.defaultProps = {params: null};

ConsumerFunction.propTypes = {
    /**
     * The ID used to identify this component in Dash callbacks.
     */
    id: PropTypes.string,

    /**
     * The path for the key in the context object to be acessed.
     * Zero-length path is not valid.
     */
    path: PropTypes.oneOfType([
        PropTypes.arrayOf(
            PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        ),
        PropTypes.string,
        PropTypes.number,
    ]),

    /**
     * The value being shared between dash and host app.
     */
    value: PropTypes.any,

    /**
     * The params used in the function evaluation.
     */
    params: PropTypes.arrayOf(PropTypes.any),

    /**
     * Dash-assigned callback that should be called to report property changes
     * to Dash, to make them available for callbacks.
     */
    setProps: PropTypes.func,

    /**
     * The context value for the Context.Consumer.
     */
    contextValue: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

InnerConsumerFunction.propTypes = ConsumerFunction.propTypes;
